import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal, Tilt, Magnetic, onRippleClick } from "../lib/interactions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoltLightning, faPlug, faGlobe, faToolbox, faScrewdriverWrench, faBoxesStacked,
  faShieldHalved, faTruckFast, faFileInvoice, faCreditCard, faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

const CATEGORIES = [
  { icon: faBoltLightning,       title: "차단기·배전함", sub: "LS·현대일렉트릭·서울전기", count: "2,400+" },
  { icon: faPlug,                title: "전선·케이블",   sub: "효성·극동·대한전선",       count: "1,800+" },
  { icon: faGlobe,               title: "네트워크·통신", sub: "D-Link·LS전선·CAT6",       count: "1,900+" },
  { icon: faToolbox,             title: "공구·계측",     sub: "HIOKI·후쿠다·Fluke",       count: "1,200+" },
  { icon: faScrewdriverWrench,   title: "배선기구",      sub: "팬더·에이스·진영",         count: "2,600+" },
  { icon: faBoxesStacked,        title: "공사자재",      sub: "덕트·배관·부속",           count: "900+" },
];

const PARTNERS = ["LS산전", "현대일렉트릭", "효성", "대한전선", "D-Link", "LS전선", "HIOKI", "팬더"];

const TRUST_BADGES = [
  { icon: faShieldHalved, t: "사업자 전용", d: "등록증 인증 승인제" },
  { icon: faTruckFast,    t: "당일 출고",   d: "15시 이전 주문 기준" },
  { icon: faFileInvoice,  t: "세금계산서",  d: "월단위 후불 거래 가능" },
  { icon: faCreditCard,   t: "안전 결제",   d: "카드·계좌이체·후불" },
];

/* Live online counter — 현실적인 흔들림을 주는 목업 */
function LiveOnline() {
  const [n, setN] = useState(84);
  useEffect(() => {
    const id = setInterval(() => setN((x) => Math.max(40, Math.min(180, x + Math.round((Math.random() - 0.4) * 8)))), 3000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="live-pill" aria-live="polite">
      <span className="live-dot" aria-hidden="true" />
      <span>지금 <strong>{n}</strong>명 접속중</span>
    </div>
  );
}

/* CountUp */
function CountUp({ end, duration = 1800, suffix = "" }: { end: number; duration?: number; suffix?: string }) {
  const [v, setV] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const tick = (t: number) => {
          const p = Math.min((t - t0) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setV(Math.floor(eased * end));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }), { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{v.toLocaleString("ko-KR")}{suffix}</span>;
}

export default function Landing() {
  return (
    <div className="grain" style={{ position: "relative" }}>
      {/* ─── Hero ─── */}
      <section style={{
        position: "relative",
        background: `radial-gradient(ellipse at top left, var(--primary-soft), transparent 60%),
                    radial-gradient(ellipse at bottom right, var(--accent-soft), transparent 60%),
                    var(--bg)`,
        overflow: "hidden",
      }}>
        <div className="container" style={{ padding: "var(--s-20) var(--s-5) var(--s-16)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: "var(--s-16)", alignItems: "center" }} className="hero-grid">
            <Reveal>
              <span className="eyebrow">B2B · 사업자 전용</span>
              <h1 style={{ fontSize: "clamp(32px, 5vw, 56px)", fontWeight: 800, margin: "16px 0", lineHeight: 1.1 }}>
                전기자재,<br />
                <span style={{ color: "var(--accent)" }}>사업자 가격</span>으로<br />
                바로 주문하세요.
              </h1>
              <p style={{ fontSize: 16, color: "var(--text-2)", maxWidth: 540, lineHeight: 1.7, marginBottom: "var(--s-8)" }}>
                차단기·케이블·배선기구·조명·공사자재까지. 사업자등록증 인증 후 현장 맞춤 단가, 당일 출고, 배송 또는 매장·현장 수령.
              </p>
              <div className="row" style={{ gap: "var(--s-3)", flexWrap: "wrap" }}>
                <Link to="/signup" className="btn btn-cta btn-lg" onMouseDown={onRippleClick}>사업자 회원가입</Link>
                <Link to="/login" className="btn btn-outline btn-lg">이미 회원이세요?</Link>
              <LiveOnline />
              </div>
              <div style={{ display: "flex", gap: "var(--s-10)", marginTop: "var(--s-12)", flexWrap: "wrap" }}>
                {[
                  { n: 12000, suffix: "+", l: "취급 품목" },
                  { n: 2400, suffix: "+", l: "거래 사업자" },
                  { n: 15, suffix: "시", l: "당일 출고 마감" },
                ].map((s) => (
                  <div key={s.l}>
                    <div style={{ fontSize: 28, fontWeight: 800, fontFamily: "var(--font-display)", letterSpacing: "-0.02em", color: "var(--primary)" }}>
                      <CountUp end={s.n} suffix={s.suffix} />
                    </div>
                    <div className="muted" style={{ fontSize: 15 }}>{s.l}</div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={200}>
              <Tilt className="card-glass" style={{ padding: "var(--s-6)", borderRadius: "var(--r-lg)" }}>
                <div className="eyebrow" style={{ marginBottom: "var(--s-2)" }}>오늘의 판매 TOP</div>
                {[
                  { sku: "LS-ABE52B-30A",  name: "LS산전 배선용 차단기", price: "18,500원" },
                  { sku: "HYO-FR-2.5",     name: "효성 FR-CV 2.5sq 케이블", price: "420원/M" },
                  { sku: "DLINK-DGS1008A", name: "D-Link 8포트 기가 스위치", price: "42,000원" },
                ].map((p, i) => (
                  <div key={p.sku} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 0", borderTop: i === 0 ? "none" : "1px solid var(--border)",
                  }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>{p.name}</div>
                      <div className="mono muted" style={{ fontSize: 15 }}>{p.sku}</div>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--accent)" }}>{p.price}</div>
                  </div>
                ))}
                <p className="muted" style={{ fontSize: 15, marginTop: "var(--s-3)" }}>※ 실 단가는 로그인 후 등급별로 표시됩니다.</p>
              </Tilt>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── Bento Categories ─── */}
      <section className="container" style={{ padding: "var(--s-16) var(--s-5)" }}>
        <Reveal>
          <h2 className="h-title" style={{ fontSize: 28 }}>취급 품목</h2>
          <p className="muted" style={{ marginBottom: "var(--s-8)" }}>6개 대분류 · 12,000개 이상 품목 상시 재고</p>
        </Reveal>

        <div className="bento">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.title} delay={i * 60}>
              <Tilt max={6} className="card bento-item">
                <div style={{ width: 48, height: 48, display: "grid", placeItems: "center", background: "var(--primary-soft)", color: "var(--primary)", borderRadius: "var(--r-sm)", marginBottom: "var(--s-3)" }}>
                  <FontAwesomeIcon icon={c.icon} style={{ width: 22, height: 22 }} />
                </div>
                <div style={{ fontWeight: 700, fontFamily: "var(--font-display)", fontSize: 17, marginBottom: "var(--s-1)" }}>{c.title}</div>
                <div className="muted" style={{ fontSize: 15, marginBottom: "var(--s-3)" }}>{c.sub}</div>
                <div className="badge" style={{ background: "var(--accent-soft)", color: "var(--accent-ink)" }}>{c.count} 품목</div>
              </Tilt>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─── Partners marquee ─── */}
      <section style={{ borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)", overflow: "hidden", background: "var(--surface)" }}>
        <div className="container" style={{ padding: "var(--s-8) var(--s-5)" }}>
          <div className="eyebrow" style={{ textAlign: "center", marginBottom: "var(--s-4)" }}>취급 브랜드</div>
          <div className="partners">
            {[...PARTNERS, ...PARTNERS].map((p, i) => (
              <div key={i} className="partner-item">{p}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works (Bento-style) ─── */}
      <section className="container" style={{ padding: "var(--s-16) var(--s-5)" }}>
        <Reveal>
          <h2 className="h-title" style={{ fontSize: 28, marginBottom: "var(--s-8)" }}>이용 절차</h2>
        </Reveal>
        <div className="bento steps">
          {[
            { n: "01", t: "회원가입", d: "사업자등록증을 업로드하고 계정을 신청합니다." },
            { n: "02", t: "관리자 승인", d: "영업일 1일 이내 심사 후 거래 등급이 확정됩니다." },
            { n: "03", t: "주문", d: "등급별 단가로 카탈로그에서 바로 주문합니다." },
            { n: "04", t: "결제·수령", d: "카드/계좌이체/세금계산서 후불. 배송 또는 현장 수령." },
          ].map((s, i) => (
            <Reveal key={s.n} delay={i * 80}>
              <div className="card" style={{ padding: "var(--s-6)" }}>
                <div style={{ fontSize: 32, fontWeight: 900, fontFamily: "var(--font-display)", color: "var(--accent)", letterSpacing: "-0.04em" }}>{s.n}</div>
                <div style={{ fontWeight: 700, margin: "var(--s-1) 0 var(--s-1)" }}>{s.t}</div>
                <div className="muted" style={{ fontSize: 15 }}>{s.d}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─── Trust badges ─── */}
      <section className="container" style={{ padding: "0 var(--s-5) var(--s-16)" }}>
        <div className="bento" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {TRUST_BADGES.map((b, i) => (
            <Reveal key={b.t} delay={i * 60}>
              <div className="card" style={{ padding: "var(--s-5)", display: "flex", gap: "var(--s-3)", alignItems: "center" }}>
                <div aria-hidden="true" style={{ background: "var(--accent-soft)", color: "var(--accent-ink)", width: 48, height: 48, display: "grid", placeItems: "center", borderRadius: "var(--r-sm)" }}>
                  <FontAwesomeIcon icon={b.icon} style={{ width: 22, height: 22 }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontFamily: "var(--font-display)", fontSize: 15 }}>{b.t}</div>
                  <div className="muted" style={{ fontSize: 15 }}>{b.d}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─── Trust / CTA ─── */}
      <section style={{ background: "var(--primary)", color: "#fff", padding: "var(--s-16) 0" }}>
        <div className="container" style={{ textAlign: "center", padding: "0 var(--s-5)" }}>
          <Reveal>
            <div className="eyebrow" style={{ color: "var(--accent)" }}>Trusted by 2,400+ businesses</div>
            <h2 style={{ fontSize: "clamp(24px, 3.5vw, 36px)", fontWeight: 800, color: "#fff", margin: "var(--s-3) 0 var(--s-4)" }}>
              지금 가입하고, 당일 주문부터 시작하세요.
            </h2>
            <Magnetic className="btn btn-cta btn-lg" onClick={() => { window.location.href = "/signup"; }} onMouseDown={onRippleClick}>
              사업자 회원가입 <FontAwesomeIcon icon={faArrowRight} style={{ width: 14, height: 14 }} />
            </Magnetic>
          </Reveal>
        </div>
      </section>

      <footer className="container" style={{ padding: "var(--s-8) var(--s-5)", fontSize: 15, color: "var(--text-muted)" }}>
        <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: "var(--s-2)" }}>
          <div>© 2026 전기닷컴 B2B Mall. 사업자등록번호 000-00-00000.</div>
          <div>Tel. 02-000-0000 · hello@jeongi.com</div>
        </div>
      </footer>

      <style>{`
        .live-pill {
          display: inline-flex; align-items: center; gap: 8px;
          margin-left: 8px;
          padding: 6px 12px;
          border-radius: var(--r-full);
          background: color-mix(in oklch, var(--success) 14%, transparent);
          color: var(--success);
          font-size: 12px; font-weight: 600;
        }
        .live-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: var(--success);
          animation: live-pulse 1.6s var(--ease-in-out) infinite;
          box-shadow: 0 0 0 0 var(--success);
        }
        @keyframes live-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 color-mix(in oklch, var(--success) 40%, transparent); }
          50% { opacity: 0.6; box-shadow: 0 0 0 8px transparent; }
        }
        .bento {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: var(--s-4);
        }
        .bento-item { padding: var(--s-5); display: flex; flex-direction: column; }
        .bento.steps { grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); }

        .partners {
          display: flex;
          gap: var(--s-10);
          animation: partners-scroll 30s linear infinite;
          width: max-content;
        }
        .partner-item {
          font-family: var(--font-display);
          font-weight: 700;
          font-size: 18px;
          color: var(--text-muted);
          letter-spacing: -0.01em;
          white-space: nowrap;
        }
        @keyframes partners-scroll {
          to { transform: translateX(-50%); }
        }

        @media (max-width: 860px) {
          .hero-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 640px) {
          .bento { grid-template-columns: 1fr; }
          .partners { animation-duration: 20s; }
        }
        @media (prefers-reduced-motion: reduce) {
          .partners { animation: none; }
        }
      `}</style>
    </div>
  );
}
